import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Trash2, Edit, Search, Info, Plus, Upload } from "lucide-react";
import axios from "axios";
import Apiurl from "../environment/Apiurl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faTimesCircle,
  faClock,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";

// Utility function to convert 24-hour time to 12-hour time with AM/PM
const formatTo12Hour = (time24) => {
  if (!time24) return "N/A";
  const [hours, minutes] = time24.split(":");
  let hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minutes} ${ampm}`;
};

const Appointment = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showVitalsModal, setShowVitalsModal] = useState(false);
  const [vitalsAppointment, setVitalsAppointment] = useState(null);
  const [vitalsData, setVitalsData] = useState({
    weight: "",
    height: "",
    bp: "",
    pulse: "",
    temperature: "",
  });
  const [isVitalsSubmitting, setIsVitalsSubmitting] = useState(false);
  const [vitalsError, setVitalsError] = useState("");
  const [vitalsDocumentType, setVitalsDocumentType] = useState("");
  const [selectedVitalsDocuments, setSelectedVitalsDocuments] = useState([]);
  const [isUploadingVitalsDocuments, setIsUploadingVitalsDocuments] =
    useState(false);
  const [vitalsDocumentError, setVitalsDocumentError] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activePage, setActivePage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [getdata, setGetdata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("All");
  const [selectedDateFilter, setSelectedDateFilter] = useState("Today");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [patientSearch, setPatientSearch] = useState("");
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState({
    _id: "",
    patient_id: "",
    first_name: "",
    last_name: "",
  });
  const [userPrivileges, setUserPrivileges] = useState({});
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);

  const booked_by_user = localStorage.getItem("name");

  // Form state for adding a new appointment
  const [addData, setAddData] = useState({
    patient_id: "",
    doctor_id: "",
    doctor_name: "",
    department: "",
    appointment_date: "",
    appointment_time: "",
    appointment_time_display: "",
    appointment_status: "Scheduled",
    remarks: "",
    booked_by: booked_by_user,
    created_by: "admin",
    created_date: new Date().toISOString(),
    updated_by: "admin",
    updated_date: new Date().toISOString(),
  });

  // Form state for updating an appointment
  const [updateData, setUpdateData] = useState({
    _id: "",
    appointment_id: "",
    patient_id: "",
    doctor_id: "",
    doctor_name: "",
    department: "",
    appointment_date: "",
    appointment_time: "",
    appointment_time_display: "",
    appointment_status: "",
    reschedule_date: "",
    cancellation_reason: "",
    remarks: "",
    booked_by: "",
    created_by: "",
    created_date: "",
    updated_by: "",
    updated_date: "",
  });

  // Fetch user privileges
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRoleId = localStorage.getItem("role_id") || "unknown";
      console.log("Fetching privileges for role_id:", userRoleId);

      if (userRoleId === "unknown") {
        console.warn("No role_id found in localStorage");
        setError(
          "No role assigned to the user. Please contact the administrator.",
        );
        setTimeout(() => setError(null), 3000);
        return;
      }

      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log("Fetched roles data:", response.data);

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid or empty roles data received");
      }

      const roleData = response.data.find(
        (role) => role.role_id === userRoleId,
      );

      if (roleData) {
        console.log("Found role data for role_id:", userRoleId, roleData);
        const privileges = {};
        roleData.pages.forEach((page) => {
          const pageName = page.page;
          const pagePrivileges = page.priviallages || page.privileges || {};
          privileges[pageName] = {
            access: page.access,
            ...pagePrivileges,
          };
        });
        console.log("Set user privileges:", privileges);
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_id:", userRoleId);
        setError("No role data found for the current user.");
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error(
        "Error fetching user privileges:",
        error.message,
        error.response?.data,
      );
      setError("Failed to fetch user privileges. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Check permission
  const hasPermission = (permission) => {
    const appointmentsPrivileges = userPrivileges["Appointments"];
    if (!appointmentsPrivileges) {
      console.log("No privileges found for Appointments page");
      return false;
    }
    console.log(
      `Checking permission '${permission}' for Appointments:`,
      appointmentsPrivileges,
    );
    if (appointmentsPrivileges.all) return true;
    const hasPerm = appointmentsPrivileges[permission] || false;
    console.log(`Permission '${permission}' result:`, hasPerm);
    return hasPerm;
  };

  // Fetch patients
  const fetchPatientDetails = async () => {
    setIsLoadingPatients(true);
    try {
      const response = await axios.get(`${Apiurl}/patient_details`);
      console.log("Fetched patient details:", response.data);
      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid patient data received");
      }
      setPatients(response.data);
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching patient details:",
        error.message,
        error.response?.data,
      );
      setError("Failed to fetch patient details. Please try again.");
      setTimeout(() => setError(null), 3000);
      setPatients([]);
    } finally {
      setIsLoadingPatients(false);
    }
  };

  // Fetch doctors
  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${Apiurl}/doctor`);
      console.log("Fetched doctor details:", response.data);
      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid doctor data received");
      }
      setDoctors(response.data);
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching doctor details:",
        error.message,
        error.response?.data,
      );
      setError("Failed to fetch doctor details. Please try again.");
      setTimeout(() => setError(null), 3000);
      setDoctors([]);
    }
  };

  // Fetch appointments
  const fetchData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Appointments page");
      setError("You do not have permission to view appointments.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("the patients in the fetch data function is :", patients);

    if (patients.length === 0 || doctors.length === 0) {
      console.warn("Patients or doctors data not loaded yet");
      return;
    }
    setLoading(true);
    try {
      const userType = localStorage.getItem("user_type") || "unknown";
      const userId = localStorage.getItem("user_id") || "";
      const username = localStorage.getItem("username") || "";
      let response;

      if (userType === "Doctor" && userId && username) {
        const doctorResponse = await axios.get(
          `${Apiurl}/doctor_by_doctor_id/${username}`,
        );
        const doctorId = doctorResponse.data?._id;
        if (!doctorId) {
          throw new Error("Doctor ID not found");
        }

        response = await axios.get(
          `${Apiurl}/doctor_appointments_details/${doctorId}`,
        );
        console.log("Appointments response:", response.data);
        const formattedData = response.data.map((appt) => {
          const patient = patients.find((p) => p._id === appt.patient_id) || {
            patient_id: "N/A",
            first_name: "Unknown",
            last_name: "",
          };
          const doctor = doctors.find((d) => d._id === appt.doctor_id) || {
            doctor_id: "N/A",
            doctor_name: "Unknown",
          };
          if (!patient._id) {
            console.warn(`No patient found for patient_id: ${appt.patient_id}`);
          }
          if (!doctor._id) {
            console.warn(`No doctor found for doctor_id: ${appt.doctor_id}`);
          }
          return {
            _id: appt._id || "",
            appointment_id: appt.appointment_id || "N/A",
            patient_id: appt.patient_id || "",
            patient_details: {
              patient_id: patient.patient_id,
              first_name: patient.first_name,
              last_name: patient.last_name,
              age: patient.age,
              gender: patient.gender,
              mobile_no: patient.mobile_no,
            },
            doctor_id: appt.doctor_id || "",
            doctor_details: {
              doctor_id: doctor.doctor_id,
              doctor_name: doctor.doctor_name,
            },
            department: appt.department || "N/A",
            appointment_date: appt.appointment_date || "N/A",
            appointment_time: appt.appointment_time || "",
            appointment_time_display: formatTo12Hour(
              appt.appointment_time || "",
            ),
            appointment_status: appt.appointment_status || "Unknown",
            remarks: appt.remarks || "",
            booked_by: appt.booked_by || "N/A",
          };
        });
        setGetdata(formattedData);
      } else {
        response = await axios.get(`${Apiurl}/appointments`);
        console.log("Appointments response:", response.data);
        const formattedData = response.data.map((appt) => {
          const patient = patients.find((p) => p._id === appt.patient_id) || {
            patient_id: "N/A",
            first_name: "Unknown",
            last_name: "",
          };
          const doctor = doctors.find((d) => d._id === appt.doctor_id) || {
            doctor_id: "N/A",
            doctor_name: "Unknown",
          };
          if (!patient._id) {
            console.warn(`No patient found for patient_id: ${appt.patient_id}`);
          }
          if (!doctor._id) {
            console.warn(`No doctor found for doctor_id: ${appt.doctor_id}`);
          }
          return {
            _id: appt._id || "",
            appointment_id: appt.appointment_id || "N/A",
            patient_id: appt.patient_id || "",
            patient_details: {
              patient_id: patient.patient_id,
              first_name: patient.first_name,
              last_name: patient.last_name,
              age: patient.age,
              gender: patient.gender,
              mobile_no: patient.mobile_no,
            },
            doctor_id: appt.doctor_id || "",
            doctor_details: {
              doctor_id: doctor.doctor_id,
              doctor_name: doctor.doctor_name,
            },
            department: appt.department || "N/A",
            appointment_date: appt.appointment_date || "N/A",
            appointment_time: appt.appointment_time || "",
            appointment_time_display: formatTo12Hour(
              appt.appointment_time || "",
            ),
            appointment_status: appt.appointment_status || "Unknown",
            remarks: appt.remarks || "",
            booked_by: appt.booked_by || "N/A",
          };
        });
        setGetdata(formattedData);
      }
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching appointments:",
        error.message,
        error.response?.data,
      );
      setError("Failed to fetch appointments. Please try again.");
      setTimeout(() => setError(null), 3000);
      setGetdata([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle patient selection
  const handlePatientSelect = (patient, mode = "add") => {
    const patientData = {
    _id: patient._id || "",
    patient_id: patient.patient_id || "",
    first_name: patient.first_name || "",
    last_name: patient.last_name || "",
    mobile_no: patient.mobile_no || "",
  };
    console.log("Selected patient:", patientData);
    setSelectedPatient(patientData);
    if (mode === "add") {
      setAddData((prev) => ({
        ...prev,
        patient_id: patientData._id,
      }));
    } else if (mode === "update") {
      setUpdateData((prev) => ({
        ...prev,
        patient_id: patientData._id,
      }));
    }
    setPatientSearch(patientData.patient_id);
    setShowPatientDropdown(false);
  };

  // Handle input changes for add form
  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "doctor_name") {
      const selectedDoc = doctors.find((doc) => doc._id === value);
      console.log("Selected doctor:", selectedDoc);
      setAddData((prev) => ({
        ...prev,
        doctor_id: selectedDoc?._id || "",
        doctor_name: selectedDoc?.doctor_name || "",
        department: selectedDoc?.department || "",
      }));
      setSelectedDepartment(selectedDoc?.department || "");
    } else if (name === "department") {
      setAddData((prev) => ({
        ...prev,
        department: value,
        doctor_id: "",
        doctor_name: "",
      }));
      setSelectedDepartment(value);
    } else if (name === "appointment_time") {
      setAddData((prev) => ({
        ...prev,
        appointment_time: value,
        appointment_time_display: formatTo12Hour(value),
      }));
    } else if (name === "patient_id") {
      const selected = patients.find((p) => p._id === value);
      console.log("Selected patient:", selected);

      if (selected) {
        setSelectedPatient({
          _id: selected._id,
          patient_id: selected.patient_id,
          first_name: selected.first_name || "Unknown",
          last_name: selected.last_name || "",
        });

        setAddData((prev) => ({
          ...prev,
          patient_id: selected._id,
        }));
      } else {
        setSelectedPatient({
          _id: "",
          patient_id: "",
          first_name: "",
          last_name: "",
        });

        setAddData((prev) => ({
          ...prev,
          patient_id: "",
        }));
      }
    } else {
      setAddData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle input changes for update form
  const handleUpdateInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "doctor_name") {
      const selectedDoc = doctors.find((doc) => doc._id === value);
      console.log("Selected doctor for update:", selectedDoc);
      setUpdateData((prev) => ({
        ...prev,
        doctor_id: selectedDoc?._id || "",
        doctor_name: selectedDoc?.doctor_name || "",
        department: selectedDoc?.department || "",
      }));
      setSelectedDepartment(selectedDoc?.department || "");
    } else if (name === "department") {
      setUpdateData((prev) => ({
        ...prev,
        department: value,
        doctor_id: "",
        doctor_name: "",
      }));
      setSelectedDepartment(value);
    } else if (name === "appointment_time") {
      setUpdateData((prev) => ({
        ...prev,
        appointment_time: value,
        appointment_time_display: formatTo12Hour(value),
      }));
    } else if (name === "appointment_status") {
      setUpdateData((prev) => ({
        ...prev,
        appointment_status: value,
        reschedule_date: value === "Rescheduled" ? prev.reschedule_date : "",
        cancellation_reason:
          value === "Cancelled" ? prev.cancellation_reason : "",
      }));
    } else {
      setUpdateData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle form submissions
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Appointments page");
      setError("You do not have permission to add appointments.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      if (!selectedPatient._id) {
        setError("Please select a patient.");
        setTimeout(() => setError(null), 3000);
        return;
      }

      const formData = {
        ...addData,
        patient_id: selectedPatient._id,
      };

      // appointment_id is generated by the backend (4H-APT-0001, 0002, ...)
      // to avoid duplicate IDs when multiple users create appointments.
      console.log("Adding appointment:", formData);
      await axios.post(`${Apiurl}/appointments`, formData);
      handleCloseAddModal();
      fetchData();
    } catch (error) {
      console.error(
        "Error adding appointment:",
        error.message,
        error.response?.data,
      );
      setError("Failed to add appointment. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Appointments page");
      setError("You do not have permission to update appointments.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (
      updateData.appointment_status === "Rescheduled" &&
      !updateData.reschedule_date
    ) {
      setError("Please select a reschedule date.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (
      updateData.appointment_status === "Cancelled" &&
      !updateData.cancellation_reason?.trim()
    ) {
      setError("Please enter a cancellation reason.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...updateData,
        patient_id: selectedPatient._id,
        appointment_date:
          updateData.appointment_status === "Rescheduled"
            ? updateData.reschedule_date
            : updateData.appointment_date,
      };

      console.log("Updating appointment:", payload);
      await axios.put(`${Apiurl}/appointments/${updateData._id}`, payload);
      fetchData();
      handleCloseUpdateModal();
    } catch (error) {
      console.error(
        "Error updating appointment:",
        error.message,
        error.response?.data,
      );
      setError("Failed to update appointment. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Appointments page");
      setError("You do not have permission to delete appointments.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid appointment ID:", deleteId);
      setError("Invalid appointment ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Deleting appointment with ID:", deleteId);
      await axios.delete(`${Apiurl}/appointments/${deleteId}`);
      fetchData();
      closeDeleteModal();
    } catch (error) {
      console.error(
        "Error deleting appointment:",
        error.message,
        error.response?.data,
      );
      setError("Failed to delete appointment. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Modal handlers
const resetAddForm = () => {
  setAddData({
    patient_id: "",
    doctor_id: "",
    doctor_name: "",
    department: "",
    appointment_date: "",
    appointment_time: "",
    appointment_time_display: "",
    appointment_status: "Scheduled",
    remarks: "",
    booked_by: booked_by_user,
    created_by: "admin",
    created_date: new Date().toISOString(),
    updated_by: "admin",
    updated_date: new Date().toISOString(),
  });

  setSelectedPatient({
    _id: "",
    patient_id: "",
    first_name: "",
    last_name: "",
    mobile_no: "",
  });

  setSelectedDepartment("");

  setPatientSearch("");
  setShowPatientDropdown(false);
};

  const openAddModal = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Appointments page");
      setError("You do not have permission to add appointments.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    resetAddForm();
    setShowAddModal(true);
  };

useEffect(() => {
  if (
    location.pathname === "/appointments" &&
    location.state?.openAddAppointment &&
    !loadingPrivileges
  ) {
    if (hasPermission("create")) {
      resetAddForm();
      setShowAddModal(true);
    } else {
      setError("You do not have permission to add appointments.");
      setTimeout(() => setError(null), 3000);
    }

    navigate("/appointments", {
      replace: true,
      state: null,
    });
  }
}, [
  location.pathname,
  location.state,
  loadingPrivileges,
  navigate,
  userPrivileges,
]);

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    resetAddForm();
  };

  const handlenewclick = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Appointments page");
      setError("You do not have permission to create new patients.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Navigating to Add Patients page");
    toggleSidebar();
    navigate("/patients/add-patients");
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleviewclick = (appointment) => {
    console.log("the appointment in the handleviewclick is : ", appointment);
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Appointments page");
      setError("You do not have permission to view appointments.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    let appointmentId = appointment._id;

    if (!appointmentId) {
      appointmentId =
        appointment.appointment_id ||
        appointment.original_appointment?._id ||
        appointment.original_appointment?.appointment_id;
    }

    if (!appointmentId) {
      console.warn("No valid appointment ID found:", appointment);
      setError("Invalid appointment ID. Cannot view details.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    console.log("Navigating to Prescription Data for appointment:", {
      appointmentId,
      appointment,
      userType: localStorage.getItem("user_type"),
    });

    toggleSidebar();
    navigate(`/appointments/prescription-data/${appointmentId}`);
  };

  const handleOpenVitalsModal = (appointment) => {
    setVitalsAppointment(appointment);
    setVitalsData({
      weight: appointment.weight || "",
      height: appointment.height || "",
      bp: appointment.bp || "",
      pulse: appointment.pulse || "",
      temperature: appointment.temperature || "",
    });
    setVitalsError("");
    setVitalsDocumentType("");
    setSelectedVitalsDocuments([]);
    setVitalsDocumentError("");
    setShowVitalsModal(true);
  };

  const handleCloseVitalsModal = () => {
    setShowVitalsModal(false);
    setVitalsAppointment(null);
    setVitalsData({
      weight: "",
      height: "",
      bp: "",
      pulse: "",
      temperature: "",
    });
    setVitalsError("");
    setVitalsDocumentType("");
    setSelectedVitalsDocuments([]);
    setVitalsDocumentError("");
  };

  const handleVitalsDocumentSelection = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedVitalsDocuments(files);
    setVitalsDocumentError("");
  };

  const handleVitalsDocumentUpload = async () => {
    if (!vitalsAppointment?._id) {
      setVitalsDocumentError("Invalid appointment. Cannot upload document.");
      return;
    }
    if (!vitalsDocumentType.trim()) {
      setVitalsDocumentError("Please select or enter the document type");
      return;
    }
    if (selectedVitalsDocuments.length === 0) {
      setVitalsDocumentError("Please select at least one document");
      return;
    }

    const uploadData = new FormData();
    uploadData.append("appointment", vitalsAppointment._id);
    uploadData.append("document_type", vitalsDocumentType.trim());
    uploadData.append(
      "uploaded_by",
      localStorage.getItem("user_name") ||
        localStorage.getItem("user_type") ||
        "system",
    );
    selectedVitalsDocuments.forEach((file) =>
      uploadData.append("documents", file),
    );

    setIsUploadingVitalsDocuments(true);
    setVitalsDocumentError("");

    try {
      await axios.post(`${Apiurl}/prescription-documents`, uploadData);
      setSelectedVitalsDocuments([]);
      setVitalsDocumentType("");

      const fileInput = document.getElementById(
        "vitals-document-input",
      );
      if (fileInput) fileInput.value = "";
    } catch (error) {
      console.error("Document upload error:", error);
      setVitalsDocumentError(
        error?.response?.data?.message || "Document upload failed",
      );
    } finally {
      setIsUploadingVitalsDocuments(false);
    }
  };

  const handleVitalsChange = (e) => {
    const { name, value } = e.target;
    setVitalsData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVitalsSubmit = async () => {
    if (!vitalsAppointment?._id) {
      setVitalsError("Invalid appointment. Cannot save details.");
      return;
    }
    try {
      setIsVitalsSubmitting(true);
      setVitalsError("");
      const response = await axios.patch(
        `${Apiurl}/appointments/${vitalsAppointment._id}/vitals`,
        vitalsData,
      );

      setGetdata((prev) =>
        prev.map((apt) =>
          apt._id === vitalsAppointment._id
            ? { ...apt, ...response.data }
            : apt,
        ),
      );

      handleCloseVitalsModal();
    } catch (error) {
      console.error("Error saving patient vitals:", error);
      setVitalsError(
        error.response?.data?.message || "Failed to save patient details.",
      );
    } finally {
      setIsVitalsSubmitting(false);
    }
  };

  const handleCloseUpdateModal = () => {
    setShowUpdateModal(false);
    setUpdateData({
      _id: "",
      appointment_id: "",
      patient_id: "",
      doctor_id: "",
      doctor_name: "",
      department: "",
      appointment_date: "",
      appointment_time: "",
      appointment_time_display: "",
      appointment_status: "",
      reschedule_date: "",
      cancellation_reason: "",
      remarks: "",
      booked_by: "",
      created_by: "",
      created_date: "",
      updated_by: "",
      updated_date: "",
    });
    setSelectedDepartment("");
    setSelectedPatient({
      _id: "",
      patient_id: "",
      first_name: "",
      last_name: "",
    });
  };

  const openDeleteModal = (id) => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Appointments page");
      setError("You do not have permission to delete appointments.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid appointment ID:", id);
      setError("Invalid appointment ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Delete Appointment modal for ID:", id);
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    console.log("Closing Delete Appointment modal");
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  const handleEdit = (appointment) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Appointments page");
      setError("You do not have permission to update appointments.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Editing appointment:", appointment);
    setUpdateData({
      _id: appointment._id || "",
      appointment_id: appointment.appointment_id || "",
      patient_id: appointment.patient_id || "",
      doctor_id: appointment.doctor_id || "",
      doctor_name: appointment.doctor_details?.doctor_name || "",
      department: appointment.department || "",
      appointment_date: appointment.appointment_date || "",
      appointment_time: appointment.appointment_time || "",
      appointment_time_display: formatTo12Hour(
        appointment.appointment_time || "",
      ),
      appointment_status: appointment.appointment_status || "",
      reschedule_date: appointment.reschedule_date || "",
      cancellation_reason: appointment.cancellation_reason || "",
      remarks: appointment.remarks || "",
      booked_by: appointment.booked_by || "",
      created_by: appointment.created_by || "",
      created_date: appointment.created_date || "",
      updated_by: appointment.updated_by || "",
      updated_date: appointment.updated_date || "",
    });
    setSelectedDepartment(appointment.department || "");
    setSelectedPatient({
      _id: appointment.patient_id || "",
      patient_id: appointment.patient_details?.patient_id || "",
      first_name: appointment.patient_details?.first_name || "",
      last_name: appointment.patient_details?.last_name || "",
    });
    setShowUpdateModal(true);
  };

  // Fetch data on mount
  useEffect(() => {
    console.log("Fetching initial data...");
    fetchUserPrivileges();
    fetchPatientDetails();
    fetchDoctors();
  }, []);

  useEffect(() => {
    const canReadAppointments =
      hasPermission("read") && patients.length > 0 && doctors.length > 0;

    if (canReadAppointments) {
      console.log(
        "Fetching appointments data due to read permission and loaded patients/doctors",
      );
      fetchData();
    } else if (!loadingPrivileges && !hasPermission("read")) {
      console.warn("Read permission denied for Appointments page");
      setError("You do not have permission to view appointments.");
      setTimeout(() => setError(null), 3000);
    } else if (patients.length === 0 || doctors.length === 0) {
      console.log(
        "Waiting for patients and doctors to load before fetching appointments",
      );
    }

    // If pharmacy billing is done in another tab/window, refresh the status
    // as soon as the user comes back to the Appointments page.
    const handleWindowFocus = () => {
      if (canReadAppointments) fetchData();
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => window.removeEventListener("focus", handleWindowFocus);
  }, [userPrivileges, loadingPrivileges, patients, doctors]);

  // Filter patients for Add / Edit appointment patient dropdown.
// When the search box is empty, show the patient list on focus.
// When the user types, filter by Patient ID, Mobile No, First Name or Last Name.
const filteredPatients = useMemo(() => {
  const term = patientSearch.trim().toLowerCase();

  if (!term) {
    return patients.slice(0, 20);
  }

  return patients
    .filter((patient) => {
      const patientId = String(patient.patient_id || "").toLowerCase();
      const mobileNo = String(patient.mobile_no || "").toLowerCase();
      const firstName = String(patient.first_name || "").toLowerCase();
      const lastName = String(patient.last_name || "").toLowerCase();
      const fullName = `${firstName} ${lastName}`.trim();

      return (
        patientId.includes(term) ||
        mobileNo.includes(term) ||
        firstName.includes(term) ||
        lastName.includes(term) ||
        fullName.includes(term)
      );
    })
    .slice(0, 20);
}, [patients, patientSearch]);

  // Filter appointments
  const filteredData = useMemo(() => {
    // Use local date instead of UTC date so the Today tab stays correct
    // for the clinic's local timezone.
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(now.getDate()).padStart(2, "0")}`;

    const normalizeText = (value) =>
      String(value || "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

    const getAppointmentType = (appointment) => {
      const department = normalizeText(appointment.department);

      if (department.includes("homeopathy")) return "homeopathy";
      if (department.includes("yoga")) return "yoga";

      // Fallback to patient ID so the filter still works even when an old
      // appointment has a blank/inconsistent department value.
      const patientId = String(
        appointment.patient_details?.patient_id || "",
      ).toUpperCase();

      if (patientId.startsWith("4HH-")) return "homeopathy";
      if (patientId.startsWith("4HY-")) return "yoga";

      return department;
    };

    let result = getdata.filter((appointment) => {
      const term = normalizeText(searchTerm);

      if (!term) return true;

      return (
        normalizeText(appointment.appointment_id).includes(term) ||
        normalizeText(appointment.patient_details?.patient_id).includes(term) ||
        normalizeText(
          `${appointment.patient_details?.first_name || ""} ${
            appointment.patient_details?.last_name || ""
          }`,
        ).includes(term) ||
        normalizeText(appointment.doctor_details?.doctor_name).includes(term) ||
        normalizeText(appointment.department).includes(term) ||
        normalizeText(appointment.appointment_date).includes(term) ||
        normalizeText(appointment.appointment_time_display).includes(term) ||
        normalizeText(appointment.appointment_status).includes(term) ||
        normalizeText(appointment.remarks).includes(term) ||
        normalizeText(appointment.booked_by).includes(term)
      );
    });

    // All / Homeopathy / Yoga filter
    if (selectedDepartment) {
      const selectedType = normalizeText(selectedDepartment);
      result = result.filter(
        (appointment) => getAppointmentType(appointment) === selectedType,
      );
    }

    // All / Scheduled / In Progress / Completed tabs
    switch (selectedStatusFilter) {
      case "Scheduled":
        result = result.filter(
          (appointment) =>
            normalizeText(appointment.appointment_status) === "scheduled",
        );
        break;

      case "In Progress":
        result = result.filter(
          (appointment) =>
            normalizeText(appointment.appointment_status) === "in progress",
        );
        break;

      case "Completed":
        result = result.filter(
          (appointment) =>
            normalizeText(appointment.appointment_status) === "completed",
        );
        break;

      case "All":
      default:
        // No status filtering. Show every appointment, including Completed,
        // Rescheduled and Cancelled appointments.
        break;
    }

    // Today / Yesterday / Last Week / Last Month / Custom date range tabs
    // Dates are compared as local YYYY-MM-DD strings (no timezone drift).
    const toLocalDateStr = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(d.getDate()).padStart(2, "0")}`;

    const getAppointmentDateStr = (appointment) =>
      String(appointment.appointment_date || "").slice(0, 10);

    switch (selectedDateFilter) {
      case "Today": {
        result = result.filter(
          (appointment) => getAppointmentDateStr(appointment) === today,
        );
        break;
      }

      case "Yesterday": {
        const yest = new Date(now);
        yest.setDate(yest.getDate() - 1);
        const yestStr = toLocalDateStr(yest);
        result = result.filter(
          (appointment) => getAppointmentDateStr(appointment) === yestStr,
        );
        break;
      }

      case "Last Week": {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 6);
        const weekAgoStr = toLocalDateStr(weekAgo);
        result = result.filter((appointment) => {
          const d = getAppointmentDateStr(appointment);
          return d >= weekAgoStr && d <= today;
        });
        break;
      }

      case "Last Month": {
        const monthAgo = new Date(now);
        monthAgo.setDate(monthAgo.getDate() - 29);
        const monthAgoStr = toLocalDateStr(monthAgo);
        result = result.filter((appointment) => {
          const d = getAppointmentDateStr(appointment);
          return d >= monthAgoStr && d <= today;
        });
        break;
      }

      case "Custom": {
        if (customStartDate && customEndDate) {
          result = result.filter((appointment) => {
            const d = getAppointmentDateStr(appointment);
            return d >= customStartDate && d <= customEndDate;
          });
        } else if (customStartDate) {
          result = result.filter(
            (appointment) =>
              getAppointmentDateStr(appointment) >= customStartDate,
          );
        } else if (customEndDate) {
          result = result.filter(
            (appointment) =>
              getAppointmentDateStr(appointment) <= customEndDate,
          );
        }
        break;
      }

      case "All":
      default:
        // No date filtering.
        break;
    }

    console.log("Filtered appointments:", result);
    return result;
  }, [
    getdata,
    searchTerm,
    selectedDepartment,
    selectedStatusFilter,
    selectedDateFilter,
    customStartDate,
    customEndDate,
  ]);

  // Pagination
  const itemsPerPage =
    rowsPerPage === "All" ? filteredData.length : rowsPerPage;
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const startIndex = (activePage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  // Reset page on search, rows per page, department, status filter, or date filter change
  useEffect(() => {
    console.log(
      "Resetting active page due to searchTerm, rowsPerPage, selectedDepartment, selectedStatusFilter, or selectedDateFilter change",
    );
    setActivePage(1);
  }, [
    searchTerm,
    rowsPerPage,
    selectedDepartment,
    selectedStatusFilter,
    selectedDateFilter,
    customStartDate,
    customEndDate,
  ]);

  // Generate page list
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (activePage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (activePage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages,
        );
      } else {
        pages.push(
          1,
          "...",
          activePage - 1,
          activePage,
          activePage + 1,
          "...",
          totalPages,
        );
      }
    }
    console.log("Generated page list:", pages);
    return pages;
  };

  const pages = getPageList();

  if (loadingPrivileges) {
    return <div className="text-center py-5">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_id:",
      localStorage.getItem("role_id"),
      "on Appointments page",
    );
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="p-3">
      <style>{`
        .btn-custom {
          background-color: #01AEBE;
          color: white;
          border: none;
        }
        .btn-custom:hover {
          background-color: #018A97;
          color: white;
        }
        .btn-group .btn {
          flex: 1;
        }
        .btn-group .btn.active {
          background-color: #01AEBE;
          color: white;
        }
      `}</style>

      <div className="row">
        <div className="col-md-3">
          <div className="btn-group w-100 crm-filter-tabs" role="group">
            <button
              className={`btn btn-outline-secondary ${
                selectedDepartment === "" ? "active" : ""
              }`}
              onClick={() => setSelectedDepartment("")}
            >
              All
            </button>
            <button
              className={`btn btn-outline-secondary ${
                selectedDepartment === "Homeopathy" ? "active" : ""
              }`}
              onClick={() => setSelectedDepartment("Homeopathy")}
            >
              Homeopathy
            </button>
            <button
              className={`btn btn-outline-secondary ${
                selectedDepartment === "Yoga" ? "active" : ""
              }`}
              onClick={() => setSelectedDepartment("Yoga")}
            >
              Yoga
            </button>
          </div>
        </div>

        <div className="col-lg-6"></div>
        <div className="col-lg-3">
          {hasPermission("create") && (
            <div className="text-end">
              <button className="btn btn-custom btn-sm" onClick={openAddModal}>
                <i className="fas fa-plus me-2"></i> Add Appointment
              </button>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {/* Date Range Tabs - left aligned, above the table toolbar (Attendance-page style) */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2 mt-2 crm-filter-tabs">
              {[
                "All",
                "Today",
                "Yesterday",
                "Last Week",
                "Last Month",
                "Custom",
              ].map((dateOption) => (
                <button
                  key={dateOption}
                  type="button"
                  className={`btn btn-sm ${
                    selectedDateFilter === dateOption
                      ? "btn-custom"
                      : "btn-outline-secondary"
                  }`}
                  onClick={() => setSelectedDateFilter(dateOption)}
                >
                  {dateOption}
                </button>
              ))}

              {selectedDateFilter === "Custom" && (
                <div className="d-flex flex-wrap align-items-center gap-2 ms-md-2">
                  <div className="d-flex align-items-center gap-1">
                    <label className="small fw-semibold text-muted mb-0">
                      From
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customStartDate}
                      max={customEndDate || undefined}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                    />
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <label className="small fw-semibold text-muted mb-0">
                      To
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={customEndDate}
                      min={customStartDate || undefined}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

      {hasPermission("read") && (
        
        <div className="card card-body mt-2">
          
          <div className="bg-white rounded shadow-sm">
            

            <div className="row mb-3 align-items-center crm-table-toolbar">
              {/* Show Entries */}
              <div className="col-md-2">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-table"></i>
                  </span>

                  <select
                    className="form-select"
                    value={rowsPerPage === "All" ? "All" : rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(
                        e.target.value === "All"
                          ? "All"
                          : parseInt(e.target.value),
                      );
                      setActivePage(1);
                    }}
                  >
                    <option value="All">Show All</option>

                    {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                      <option key={n} value={n}>
                        Show {n}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Center Tabs */}
              <div className="col-md-7">
                <div className="d-flex justify-content-center align-items-center gap-2 flex-wrap crm-filter-tabs">
                  {["All", "Scheduled", "In Progress", "Completed"].map(
                    (status) => (
                      <button
                        key={status}
                        type="button"
                        className={`btn btn-sm px-3 ${
                          selectedStatusFilter === status
                            ? "btn-custom"
                            : "btn-outline-secondary"
                        }`}
                        onClick={() => setSelectedStatusFilter(status)}
                      >
                        {status}
                      </button>
                    ),
                  )}
                </div>
              </div>

              {/* Search */}
              <div className="col-md-3">
                <div className="d-flex justify-content-end align-items-center">
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control pe-5"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                      }}
                      style={{
                        width: "250px",
                        paddingRight: "2.5rem",
                      }}
                    />

                    <Search
                      size={16}
                      className="position-absolute text-muted"
                      style={{
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {loading && <div className="text-center py-3">Loading...</div>}

            <div className="table-responsive">
              <table className="table table-hover">
                <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                  <tr>
                    <th>S.No</th>
                    <th>Appointment ID</th>
                    <th>Patient ID</th>
                    <th>Patient</th>
                    <th>Doctor</th>
                    <th>Department</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length > 0 ? (
                    paginatedData.map((appointment, idx) => (
                      <tr key={appointment._id || idx} className="align-middle">
                        <td>{startIndex + idx + 1}</td>
                        <td>{appointment.appointment_id || "N/A"}</td>
                        <td>
                          {appointment.patient_details?.patient_id || "N/A"}
                        </td>
                        <td>
                          {appointment.patient_details?.first_name
                            ? `${appointment.patient_details.first_name} ${
                                appointment.patient_details.last_name || ""
                              }`
                            : "Unknown"}
                        </td>
                        <td>
                          {appointment.doctor_details?.doctor_name || "Unknown"}
                        </td>
                        <td>{appointment.department}</td>
                        <td>{appointment.appointment_date}</td>
                        <td>{appointment.appointment_time_display}</td>
                        <td>
                          {(() => {
                            const statusKey =
                              appointment.appointment_status?.toLowerCase();
                            const statusStyles = {
                              scheduled: {
                                className: "bg-secondary text-white",
                                icon: faClock,
                                label: "Scheduled",
                              },
                              "in progress": {
                                className: "bg-warning text-dark",
                                icon: faClock,
                                label: "In Progress",
                              },
                              completed: {
                                className: "bg-success text-white",
                                icon: faCheckCircle,
                                label: "Completed",
                              },
                              cancelled: {
                                className: "bg-danger text-white",
                                icon: faTimesCircle,
                                label: "Cancelled",
                              },
                              rescheduled: {
                                className: "bg-warning text-dark",
                                icon: faClock,
                                label: "Rescheduled",
                              },
                            };
                            const status = statusStyles[statusKey] || {
                              className: "bg-secondary text-muted",
                              icon: faQuestionCircle,
                              label: "Unknown",
                            };
                            return (
                              <span
                                className={`badge rounded-pill px-3 py-2 fw-semibold d-flex align-items-center ${status.className}`}
                              >
                                <FontAwesomeIcon
                                  icon={status.icon}
                                  className="me-2"
                                />
                                {status.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          {hasPermission("update") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Edit"
                              onClick={() => handleEdit(appointment)}
                              disabled={!appointment._id}
                            >
                              <Edit size={16} className="text-primary" />
                            </button>
                          )}
                          {hasPermission("delete") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Delete"
                              onClick={() => openDeleteModal(appointment._id)}
                              disabled={!appointment._id}
                            >
                              <Trash2 size={16} className="text-danger" />
                            </button>
                          )}
                          {hasPermission("update") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Add Patient Vitals"
                              onClick={() => handleOpenVitalsModal(appointment)}
                              disabled={!appointment._id}
                            >
                              <Plus size={16} className="text-success" />
                            </button>
                          )}
                          <button
                            className="btn btn-sm p-1 border-0"
                            title="View"
                            onClick={() => handleviewclick(appointment)}
                            disabled={
                              !appointment._id && !appointment.appointment_id
                            }
                          >
                            <Info size={16} className="text-muted" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="text-center py-4">
                        {loading ? (
                          "Loading..."
                        ) : (
                          <div className="text-muted">
                            <i className="fas fa-search fa-2x mb-2"></i>
                            <br />
                            No appointments found matching your search criteria
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="container">
              <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    activePage > 1 && setActivePage(activePage - 1)
                  }
                  disabled={activePage === 1}
                >
                  <i className="fas fa-chevron-left"></i>
                  <span className="d-none d-sm-inline">Previous</span>
                </button>

                <div
                  className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                  style={{ maxWidth: "100%" }}
                >
                  {pages.map((page, index) => (
                    <button
                      key={index}
                      className={`btn btn-sm px-3 flex-shrink-0 ${
                        page === activePage
                          ? "text-white"
                          : "btn-outline-secondary"
                      }`}
                      style={
                        page === activePage
                          ? { backgroundColor: "#01AEBE", border: "none" }
                          : {}
                      }
                      onClick={() => page !== "..." && setActivePage(page)}
                      disabled={page === "..."}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="btn btn-outline-secondary d-flex align-items-center gap-2"
                  onClick={() =>
                    activePage < totalPages && setActivePage(activePage + 1)
                  }
                  disabled={activePage === totalPages}
                >
                  <span className="d-none d-sm-inline">Next</span>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasPermission("create") && (
        <div
          className={`modal fade ${showAddModal ? "show" : ""}`}
          style={{ display: showAddModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="addAppointmentModalLabel"
          aria-hidden={!showAddModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Add Appointment</h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseAddModal}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleAddSubmit}>
                  <div className="row g-2">
                    <div className="col-md-6">
  <label className="form-label fw-semibold">
    Patient ID
  </label>

  <div className="position-relative">
    <input
      type="text"
      className="form-control form-control-sm"
      placeholder="Search Patient ID / Mobile / Name"
      value={patientSearch}
      autoComplete="off"
      onChange={(e) => {
        const value = e.target.value;

        setPatientSearch(value);

        // Clear selected patient when user changes search
        if (
          selectedPatient.patient_id &&
          value !== selectedPatient.patient_id
        ) {
          setSelectedPatient({
            _id: "",
            patient_id: "",
            first_name: "",
            last_name: "",
            mobile_no: "",
          });

          setAddData((prev) => ({
            ...prev,
            patient_id: "",
          }));
        }

        setShowPatientDropdown(true);
      }}
      onFocus={() => {
        setShowPatientDropdown(true);
      }}
      onClick={() => {
        setShowPatientDropdown(true);
      }}
      required
    />

    {showPatientDropdown && (
      <div
        className="position-absolute bg-white border rounded shadow-sm w-100"
        style={{
          top: "100%",
          left: 0,
          zIndex: 1055,
          maxHeight: "250px",
          overflowY: "auto",
        }}
      >
        {isLoadingPatients ? (
          <div className="p-3 text-muted text-center">
            Searching patients...
          </div>
        ) : filteredPatients.length > 0 ? (
          filteredPatients.map((patient) => (
            <button
              key={patient._id}
              type="button"
              className="dropdown-item text-start p-2 border-bottom"
              onClick={() => handlePatientSelect(patient, "add")}
            >
              <div className="fw-semibold">
                {patient.patient_id || "No Patient ID"}
              </div>

              <div className="small text-dark">
                {patient.first_name || ""}{" "}
                {patient.last_name || ""}
              </div>

              <div className="small text-muted">
                Mobile: {patient.mobile_no || "N/A"}
              </div>
            </button>
          ))
        ) : (
          <div className="p-3 text-muted text-center">
            No patient found
          </div>
        )}
      </div>
    )}
  </div>
</div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Patient</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        name="patient_display"
                        readOnly
                        value={
                          selectedPatient.first_name
                            ? `${selectedPatient.first_name} ${
                                selectedPatient.last_name || ""
                              }`
                            : "Select a patient"
                        }
                        placeholder="Patient"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Department
                      </label>
                      <select
                        className="form-control form-control-sm"
                        name="department"
                        required
                        value={addData.department}
                        onChange={handleAddInputChange}
                      >
                        <option value="">Select Department</option>
                        {[...new Set(doctors.map((doc) => doc.department))].map(
                          (dept, index) => (
                            <option key={index} value={dept}>
                              {dept}
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Doctor Name
                      </label>
                      <select
                        className="form-control form-control-sm"
                        name="doctor_name"
                        required
                        value={addData.doctor_id}
                        onChange={handleAddInputChange}
                        disabled={!selectedDepartment}
                      >
                        <option value="">Select Doctor</option>
                        {doctors
                          .filter(
                            (doc) => doc.department === selectedDepartment,
                          )
                          .map((doc, index) => (
                            <option key={index} value={doc._id}>
                              {doc.doctor_id} - {doc.doctor_name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Appointment Date
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        name="appointment_date"
                        value={addData.appointment_date}
                        onChange={handleAddInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Appointment Time
                      </label>
                      <input
                        type="time"
                        className="form-control form-control-sm"
                        name="appointment_time"
                        value={addData.appointment_time}
                        onChange={handleAddInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Status</label>
                      <select
                        className="form-control form-control-sm"
                        name="appointment_status"
                        required
                        value={addData.appointment_status}
                        onChange={handleAddInputChange}
                      >
                        <option value="Scheduled">Scheduled</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Booked By
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        name="booked_by"
                        value={booked_by_user}
                        readOnly
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold">Remarks</label>
                      <textarea
                        className="form-control form-control-sm"
                        name="remarks"
                        rows="2"
                        value={addData.remarks}
                        onChange={handleAddInputChange}
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-center gap-4 mt-3">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary btn-sm"
                      onClick={handlenewclick}
                      disabled={loading}
                    >
                      New Patient
                    </button>
                    <button
                      type="submit"
                      className="btn btn-custom btn-sm"
                      disabled={loading}
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasPermission("update") && (
        <div
          className={`modal fade ${showUpdateModal ? "show" : ""}`}
          style={{ display: showUpdateModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="updateAppointmentModalLabel"
          aria-hidden={!showUpdateModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Edit Appointment</h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseUpdateModal}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleUpdateSubmit}>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Appointment ID
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        name="appointment_id"
                        value={updateData.appointment_id}
                        readOnly
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Patient</label>
                      <div style={{ position: "relative" }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          name="patient_name"
                          value={
                            selectedPatient.first_name
                              ? `${selectedPatient.first_name} ${
                                  selectedPatient.last_name || ""
                                }`
                              : "Select a patient"
                          }
                          onChange={(e) => {
                            setUpdateData((prev) => ({
                              ...prev,
                              patient_name: e.target.value,
                            }));
                            setShowPatientDropdown(true);
                          }}
                          onFocus={() => setShowPatientDropdown(true)}
                          onBlur={() =>
                            setTimeout(() => setShowPatientDropdown(false), 200)
                          }
                          placeholder="Search patient..."
                        />
                        {showPatientDropdown && (
                          <div
                            style={{
                              position: "absolute",
                              zIndex: 1000,
                              width: "100%",
                              maxHeight: "200px",
                              overflowY: "auto",
                              backgroundColor: "white",
                              border: "1px solid #ddd",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            }}
                          >
                            {isLoadingPatients ? (
                              <div className="dropdown-item">Loading...</div>
                            ) : filteredPatients.length > 0 ? (
                              filteredPatients.map((patient, index) => (
                                <div
                                  key={index}
                                  className="dropdown-item"
                                  style={{ cursor: "pointer" }}
                                  onMouseDown={() =>
                                    handlePatientSelect(patient, "update")
                                  }
                                >
                                  {patient.patient_id} - {patient.first_name}{" "}
                                  {patient.last_name || ""}
                                </div>
                              ))
                            ) : (
                              <div className="dropdown-item text-muted">
                                No patients found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Department
                      </label>
                      <select
                        className="form-control form-control-sm"
                        name="department"
                        value={updateData.department}
                        onChange={handleUpdateInputChange}
                        required
                      >
                        <option value="">Select Department</option>
                        {[...new Set(doctors.map((doc) => doc.department))].map(
                          (dept, index) => (
                            <option key={index} value={dept}>
                              {dept}
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Doctor Name
                      </label>
                      <select
                        className="form-control form-control-sm"
                        name="doctor_name"
                        value={updateData.doctor_id}
                        onChange={handleUpdateInputChange}
                        disabled={!selectedDepartment}
                        required
                      >
                        <option value="">Select Doctor</option>
                        {doctors
                          .filter(
                            (doc) => doc.department === selectedDepartment,
                          )
                          .map((doc, index) => (
                            <option key={index} value={doc._id}>
                              {doc.doctor_id} - {doc.doctor_name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Appointment Date
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        name="appointment_date"
                        value={updateData.appointment_date}
                        onChange={handleUpdateInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Appointment Time
                      </label>
                      <input
                        type="time"
                        className="form-control form-control-sm"
                        name="appointment_time"
                        value={updateData.appointment_time}
                        onChange={handleUpdateInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Status</label>
                      <select
                        className="form-control form-control-sm"
                        name="appointment_status"
                        value={updateData.appointment_status}
                        onChange={handleUpdateInputChange}
                        required
                      >
                        <option value="Scheduled">Scheduled</option>
                        <option value="Rescheduled">Reschedule</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>

                    {updateData.appointment_status === "Rescheduled" && (
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Reschedule Date
                        </label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          name="reschedule_date"
                          value={updateData.reschedule_date}
                          onChange={handleUpdateInputChange}
                          min={new Date().toISOString().split("T")[0]}
                          required
                        />
                      </div>
                    )}

                    {updateData.appointment_status === "Cancelled" && (
                      <div className="col-12">
                        <label className="form-label fw-semibold">
                          Cancellation Reason
                        </label>
                        <textarea
                          className="form-control form-control-sm"
                          name="cancellation_reason"
                          rows="2"
                          placeholder="Enter cancellation reason"
                          value={updateData.cancellation_reason}
                          onChange={handleUpdateInputChange}
                          required
                        />
                      </div>
                    )}

                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        Booked By
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        name="booked_by"
                        value={updateData.booked_by}
                        onChange={handleUpdateInputChange}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-semibold">Remarks</label>
                      <textarea
                        className="form-control form-control-sm"
                        name="remarks"
                        rows="2"
                        value={updateData.remarks}
                        onChange={handleUpdateInputChange}
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-center gap-2 mt-3">
                    <button
                      type="submit"
                      className="btn btn-custom btn-sm"
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deleteAppointmentModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{ color: "#dc2626", fontSize: "20px" }}
                    ></i>
                  </span>
                </div>
                <h5 className="modal-title" id="deleteAppointmentModalLabel">
                  Delete Appointment
                </h5>
                <p style={{ marginTop: "10px" }}>
                  Are you sure you want to delete this appointment?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? "Deleting..." : "Yes, Delete It!"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showVitalsModal && (
        <div
          className={`modal fade ${showVitalsModal ? "show" : ""}`}
          style={{
            display: showVitalsModal ? "block" : "none",
            background: "rgba(0,0,0,0.5)",
          }}
          tabIndex="-1"
          aria-labelledby="vitalsModalLabel"
          aria-hidden={!showVitalsModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
             style={{ maxWidth: "600px", width: "100%" }}
          >
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header py-2">
                <div>
                  <h6
                    className="modal-title text-dark mb-0"
                    id="vitalsModalLabel"
                  >
                    Patient Basic Details
                  </h6>
                  {/* <small className="text-muted">
                    {vitalsAppointment?.patient_details?.first_name
                      ? `${vitalsAppointment.patient_details.first_name} ${
                          vitalsAppointment.patient_details.last_name || ""
                        }`
                      : "Unknown"}{" "}
                    · {vitalsAppointment?.patient_details?.patient_id || "N/A"}
                  </small> */}
                </div>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={handleCloseVitalsModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <div className="border rounded bg-light px-2 py-2 mb-3">
                  <div className="row g-2 small">
                    <div className="col-6">
                      <span className="fw-semibold text-secondary">
                        Patient ID:
                      </span>{" "}
                      {vitalsAppointment?.patient_details?.patient_id || "N/A"}
                    </div>
                    <div className="col-6">
                      <span className="fw-semibold text-secondary">
                        Age / Sex:
                      </span>{" "}
                      {vitalsAppointment?.patient_details?.age || "N/A"} /{" "}
                      {vitalsAppointment?.patient_details?.gender || "N/A"}
                    </div>
                    <div className="col-6">
                      <span className="fw-semibold text-secondary">
                        Mobile:
                      </span>{" "}
                      {vitalsAppointment?.patient_details?.mobile_no || "N/A"}
                    </div>
                    <div className="col-6">
                      <span className="fw-semibold text-secondary">
                        Doctor:
                      </span>{" "}
                      {vitalsAppointment?.doctor_details?.doctor_name || "N/A"}
                    </div>
                  </div>
                </div>

                {vitalsError && (
                  <div className="alert alert-danger py-1 px-2 small mb-2">
                    {vitalsError}
                  </div>
                )}

                <div className="row g-2">
                  {/* Weight */}
                  <div className="col-6">
                    <label className="small fw-semibold">Weight:</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="weight"
                      value={vitalsData.weight}
                      onChange={handleVitalsChange}
                      placeholder="Enter weight"
                    />
                  </div>

                  {/* Height */}
                  <div className="col-6">
                    <label className="small fw-semibold">Height:</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="height"
                      value={vitalsData.height}
                      onChange={handleVitalsChange}
                      placeholder="Enter height"
                    />
                  </div>

                  {/* BP */}
                  <div className="col-6">
                    <label className="small fw-semibold">BP:</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="bp"
                      value={vitalsData.bp}
                      onChange={handleVitalsChange}
                      placeholder="Enter BP"
                    />
                  </div>

                  {/* Pulse */}
                  <div className="col-6">
                    <label className="small fw-semibold">Pulse:</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="pulse"
                      value={vitalsData.pulse}
                      onChange={handleVitalsChange}
                      placeholder="Enter pulse"
                    />
                  </div>

                  {/* Temperature */}
                  <div className="col-6">
                    <label className="small fw-semibold">Temperature:</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      name="temperature"
                      value={vitalsData.temperature}
                      onChange={handleVitalsChange}
                      placeholder="Enter temperature"
                    />
                  </div>
                </div>

                <hr className="my-3" />

                <div>
                  <h6 className="small fw-bold d-flex align-items-center gap-2 mb-2">
                    <Upload size={14} />
                    Upload Patient Document
                  </h6>

                  {vitalsDocumentError && (
                    <div className="alert alert-danger py-1 px-2 small mb-2">
                      {vitalsDocumentError}
                    </div>
                  )}

                  <div className="row g-2 align-items-end">
                    <div className="col-12 col-md-4">
                      <label className="small fw-semibold">
                        Document Type
                      </label>
                      <input
                        type="text"
                        list="vitals-document-type-options"
                        className="form-control form-control-sm"
                        value={vitalsDocumentType}
                        onChange={(e) => {
                          setVitalsDocumentType(e.target.value);
                          setVitalsDocumentError("");
                        }}
                        placeholder="e.g. Blood Report"
                      />
                      <datalist id="vitals-document-type-options">
                        <option value="Blood Report" />
                        <option value="Urine Report" />
                        <option value="Lab Report" />
                        <option value="Scan / Imaging Report" />
                        <option value="X-Ray Report" />
                        <option value="ECG Report" />
                        <option value="Previous Prescription" />
                        <option value="Discharge Summary" />
                        <option value="Other" />
                      </datalist>
                    </div>

                    <div className="col-12 col-md-5">
                      <label className="small fw-semibold">
                        Choose File(s)
                      </label>
                      <input
                        id="vitals-document-input"
                        type="file"
                        className="form-control form-control-sm"
                        multiple
                        accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.ppt,.pptx"
                        onChange={handleVitalsDocumentSelection}
                      />
                    </div>

                    <div className="col-12 col-md-3">
                      <button
                        type="button"
                        className="btn btn-sm btn-custom w-100 d-inline-flex align-items-center justify-content-center gap-1"
                        onClick={handleVitalsDocumentUpload}
                        disabled={
                          isUploadingVitalsDocuments ||
                          selectedVitalsDocuments.length === 0 ||
                          !vitalsDocumentType.trim()
                        }
                      >
                        <Upload size={14} />
                        {isUploadingVitalsDocuments ? "Uploading..." : "Upload"}
                      </button>
                    </div>
                  </div>

                  {selectedVitalsDocuments.length > 0 && (
                    <div className="small text-secondary mt-2">
                      Selected:{" "}
                      {selectedVitalsDocuments
                        .map((file) => file.name)
                        .join(", ")}
                    </div>
                  )}
                </div>
              </div>
              <div
                className="modal-footer py-2"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={handleCloseVitalsModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-custom btn-sm"
                  onClick={handleVitalsSubmit}
                  disabled={isVitalsSubmitting}
                >
                  {isVitalsSubmitting ? "Saving..." : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showAddModal ||
        showUpdateModal ||
        showDeleteModal ||
        showVitalsModal) && <div className="modal-backdrop fade show"></div>}
    </div>
  );
};

export default Appointment;